import { sdk } from '@/lib/sdk'
import type { MediaConfirmResult, MediaUpload } from '@/types/courses'
import { announcePlanLimit, isStorageLimitError } from '@/lib/planLimitNotice'

export const VIDEO_MAX_BYTES = 500 * 1024 * 1024
export const FILE_MAX_BYTES = 50 * 1024 * 1024
export const VIDEO_MIMES = ['video/mp4', 'video/webm', 'video/quicktime']
export const FILE_MIMES = [
  'application/pdf', 'application/zip', 'application/x-zip-compressed',
  'application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
]
export const VIDEO_ACCEPT = '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime'
export const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.png,.jpg,.jpeg,.gif,.webp'

export type MediaKind = 'video' | 'file'

/** `null` when the file may be sent; otherwise which rule it breaks. The server checks again. */
export const checkMediaFile = (kind: MediaKind, file: File): 'tooBig' | 'type' | null => {
  const max = kind === 'video' ? VIDEO_MAX_BYTES : FILE_MAX_BYTES
  if (file.size > max) return 'tooBig'
  const allowed = kind === 'video' ? VIDEO_MIMES : FILE_MIMES
  return allowed.includes(file.type) ? null : 'type'
}

export class UploadCanceled extends Error {}

/** PUT straight to storage with the exact headers and size the API signed; reports progress. */
export const putToStorage = (upload: MediaUpload, file: File, onProgress: (percent: number) => void, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(upload.method || 'PUT', upload.uploadUrl)
    for (const [name, value] of Object.entries(upload.headers || {})) xhr.setRequestHeader(name, value)
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100)) }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('storage')))
    xhr.onerror = () => reject(new Error('storage'))
    xhr.onabort = () => reject(new UploadCanceled())
    signal.addEventListener('abort', () => xhr.abort())
    xhr.send(file)
  })

/** upload-url, then the direct PUT, then confirm. Nothing changes on the lesson until confirm succeeds. */
export const uploadLessonMedia = async (
  lessonId: string, file: File, onProgress: (percent: number) => void, signal: AbortSignal,
): Promise<MediaConfirmResult> => {
  const upload = (await sdk.courses.requestMediaUpload(lessonId, { name: file.name, mimeType: file.type, size: file.size }).catch((error: unknown) => {
    /* F-34 `media.storageGb`: the form shows the refusal; the toast offers the plan change. */
    if (isStorageLimitError(error)) announcePlanLimit({ kind: 'storage' })
    throw error
  })) as MediaUpload
  if (signal.aborted) throw new UploadCanceled()
  await putToStorage(upload, file, onProgress, signal)
  return (await sdk.courses.confirmMedia(lessonId, { key: upload.key, name: upload.name || file.name, size: file.size })) as MediaConfirmResult
}

export const formatBytes = (bytes: number, locale?: string): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1 }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value)} ${units[unit]}`
}
